// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import { qtyFieldStyles } from "@synd/App/Components/Qty/QtyField";
import { createHorizontalSmallHeadingAndTextStyles } from "@synd/App/SharedStyles/smallHeadingAndTextStyles";
import { FormFieldLabel } from "@synd/MobiusOverrides/FormField";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { color, getSystemColor, xScreen } from "@synd/XDL/Utilities";
import { css } from "styled-components";

qtyFieldStyles.actionButton = {
    css: css`
        height: 100%;
        padding: 6px;
        display: flex;
        align-items: center;
        transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
        border: none;
        background-color: ${color("common.ui1")};
        &:disabled {
            opacity: 0.33;
        }
        ${IconWrapper} {
            color: ${color("common.ui7")};
            &,
            svg {
                width: 13px;
                height: 10px;
            }
        }
        &.qty-field-decrease {
            padding: 8px;
            ${IconWrapper}, svg {
                width: 11px;
            }
        }
        &:hover:not(:disabled),
        &:focus {
            ${IconWrapper} {
                color: ${color("secondary")};
            }
        }
    `,
};

qtyFieldStyles.wrapper = {
    css: css`
        background: ${color("common.ui1")};
        display: flex;
        align-items: center;
        min-width: 97px;
        width: 97px;
        overflow: hidden;
        ${({ qtySizeVariant }: any) =>
            qtySizeVariant === "large"
                ? css`
                      height: 34px;
                      border-radius: 18px;
                  `
                : css`
                      height: 28px;
                      border-radius: 13px;
                  `};
        &:not(${FormFieldLabel}) {
            border: 1px solid ${getSystemColor("common.border")};
            &:not(:focus-within):has(input:not(:disabled)):hover {
                border: 1px solid ${color("common.ui11")};
            }
            &:has(input:not(:disabled)):focus-within {
                border: 1px solid ${color("common.ui10")};
            }
        }
        overflow-x: hidden;
    `,
};

qtyFieldStyles.editableQuantityTextField = {
    sizeVariant: "small",
    mergeCss: true,
    cssOverrides: {
        formField: css`
            height: 100%;
            ${TypographyStyle} {
                text-align: center;
            }

            &&& {
                input {
                    &,
                    &:hover:not(:disabled),
                    &:focus:not(:disabled) {
                        border: none !important;
                        border-radius: 0;
                        outline: none;
                        box-shadow: none;
                        padding: 0;
                    }
                    &:disabled {
                        background: none;
                    }
                }
            }
        `,
        formInputWrapper: css`
            &,
            &:hover:not(:disabled),
            &:focus:not(:disabled) {
                height: 100%;
                border: none;
                box-shadow: none;
                padding: 0 !important;
            }
        `,
        inputSelect: css`
            font-size: 14px;
            line-height: 16px;
            text-align: center;
            height: 100%;
            padding: 0 !important;
        `,
    },
};

qtyFieldStyles.container = {
    css: css`
        display: flex;
        justify-content: center;
        flex-direction: column;
    `,
};

qtyFieldStyles.questionIconStyles = {
    size: 14,
    css: css`
        opacity: 0.66;
        color: ${color("info.main")};
        &:hover {
            opacity: 1;
            color: ${color("common.ui8")};
        }
    `,
};

qtyFieldStyles.multipleSaleQtyToolTip = {
    mergeCss: true,
    extendedStyles: {
        triggerWrapper: {
            css: css`
                margin: 0;
            `,
        },
        tooltipContent: {
            css: css`
                min-width: 256px;
            `,
        },
    },
};

qtyFieldStyles.qtyLabelNotEditable = {
    as: "label",
    size: 12,
    weight: fontConfig.weight.regular,
    lineHeight: "17px",
    css: css`
        margin-bottom: 4px;
        display: none;
        ${xScreen("lte", "md")} {
            display: block;
        }
        @media print {
            &&& {
                display: block;
                font-size: 14px;
                line-height: 16px;
            }
        }
    `,
};

qtyFieldStyles.valueText = {
    size: 14,
    weight: fontConfig.weight.bold,
    css: css`
        display: flex;
        gap: 4px;
        align-items: center;
        line-height: 20px;
    `,
};

qtyFieldStyles.qtyLabel = {
    size: 12,
    lineHeight: "14px",
    weight: fontConfig.weight.regular,
    css: css`
        display: none;
        ${xScreen("lte", "md")} {
            margin-bottom: 4px;
            display: block;
        }
    `,
};

qtyFieldStyles.qtyLabelContainer = {
    css: css`
        display: flex;
        align-items: center;
        gap: 5px;
    `,
};
qtyFieldStyles.tooltipBody = {
    css: css`
        display: flex;
        gap: 8px;
    `,
};
qtyFieldStyles.headingAndTextStyles = {
    ...createHorizontalSmallHeadingAndTextStyles({
        heading: {
            color: "common.ui1",
            css: css`
                && {
                    margin-bottom: 0;
                }
            `,
        },
        text: {
            color: "common.ui1",
            css: css`
                && {
                    margin-bottom: 0;
                }
            `,
        },
    }),
};

qtyFieldStyles.tooltipTextWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 5px;
    `,
};

qtyFieldStyles.multipleSaleQtyWrapper = {
    css: css`
        line-height: 14px;
        display: flex;
        padding-top: 1px;
    `,
};
